/**
 * types/brief-builder.ts — TypeScript types for the Brief Builder module.
 */

// ── Session stages ────────────────────────────────────────────────────────────

export type BriefStage =
  | 'intake'
  | 'interpreting'
  | 'interpreted'
  | 'recommending'
  | 'reviewing'
  | 'validating'
  | 'validated'
  | 'configured'
  | 'exporting'
  | 'complete'
  | 'error'

// ── Domain types ──────────────────────────────────────────────────────────────

export type ModuleType = 'dashboards' | 'ai_assistant' | 'surveys'

export type SubcategoryType = 'market' | 'product_category' | 'persona' | 'segment' | 'custom'

export const SUBCATEGORY_TYPE_LABELS: Record<SubcategoryType, string> = {
  market:           'Markets',
  product_category: 'Product Lines',
  persona:          'Personas',
  segment:          'Segments',
  custom:           'Custom',
}

export const SURVEY_ALLOWED_TYPES = [
  'Consumer Behaviour',
  'Current Trends',
  'Market Conditions',
  'Product Survey',
] as const

export interface SubcategoryDraft {
  id: string
  name: string
  type: SubcategoryType
  filters: Record<string, string>
}

export interface SurveyModuleConfig {
  creator_prompt_id:  string
  analyzer_prompt_id: string
  survey_types:       string[]
  num_questions:      number
}

export interface AIAssistantConfig {
  prompt_id:          string
  vector_store_id:    string
  wizard_prompt_id:   string
  starting_questions: string[]
}

export interface WidgetInstanceConfig {
  /** Custom display title — overrides the widget's default name in the header */
  title?: string
  /** Topic/category filter for newsfeed widgets (must be from newsfeed_type_options) */
  newsfeed_type?: string
  /** Short help text shown on hover */
  tooltip?: string
  /** Number of grid slots this instance occupies (1-6); overrides widget default_slots */
  slots?: number
}

export interface Stakeholder {
  role: string
  department: string
  goals: string[]
  data_needs: string[]
}

export interface DashboardDraft {
  id: string
  name: string
  family: string
  widget_ids: string[]
  /** Per-slot widget configuration, keyed by widget_id */
  widget_configs: Record<string, WidgetInstanceConfig>
  subcategories: SubcategoryDraft[]
  rationale?: string
  human_edited?: boolean
}

export interface WidgetSelection {
  widget_id: string
  dashboard_id: string
  rationale: string
  confidence: number
}

export interface ExtractedRequirements {
  tenant_name: string
  industry: string
  markets: string[]
  stakeholders: Stakeholder[]
  business_goals: string[]
  use_cases: string[]
  data_sources_mentioned: string[]
  suggested_modules: ModuleType[]
  confidence: number
  assumptions: string[]
  missing_fields: string[]
}

export interface AIRecommendation {
  modules: ModuleType[]
  dashboards: DashboardDraft[]
  widget_selections: WidgetSelection[]
  open_questions: string[]
  assumptions: string[]
  confidence: number
  rationale: Record<string, string>
}

export interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
}

// ── Full session ──────────────────────────────────────────────────────────────

export interface BriefSession {
  id: string
  stage: BriefStage
  created_at: string
  updated_at: string
  label: string
  brief_input?: {
    tenant_name: string
    industry: string
    raw_brief: string
    stakeholders_raw: string
    markets: string[]
    languages: string[]
    goals_raw: string
    uploaded_notes?: string
  }
  extracted_requirements?: ExtractedRequirements
  open_questions: string[]
  clarification_answers: Record<string, string>
  assumptions: string[]
  extraction_confidence: number
  recommendation?: AIRecommendation
  approved_structure?: {
    modules: ModuleType[]
    dashboards: DashboardDraft[]
    clarification_answers: Record<string, string>
  }
  validation_result?: ValidationResult
  ai_assistant_config?: AIAssistantConfig
  survey_config?: SurveyModuleConfig
  final_config?: Record<string, unknown>
  error?: string
}

export interface BriefSessionSummary {
  id: string
  label: string
  stage: BriefStage
  created_at: string
  updated_at: string
}

// ── API request/response payloads ──────────────────────────────────────────────

export interface CreateSessionPayload {
  tenant_name: string
  website: string
  industry: string
  raw_brief: string
  stakeholders_raw: string
  markets: string[]
  languages: string[]
  goals_raw: string
  uploaded_notes?: string
}

export interface SubmitClarificationsPayload {
  answers: Record<string, string>
}

export interface UpdateStructurePayload {
  modules: ModuleType[]
  dashboards: DashboardDraft[]
  clarification_answers: Record<string, string>
}

export interface SubmitModuleConfigPayload {
  ai_assistant_config?: Partial<AIAssistantConfig>
  survey_config?: Partial<SurveyModuleConfig>
}

export interface ExportResponse {
  session_id: string
  yaml_content: string
  json_content: string
  validation_valid: boolean
  validation_warnings: string[]
}

// ── Widget library ────────────────────────────────────────────────────────────

export interface WidgetDefinition {
  id: string
  name: string
  category: string
  description: string
  slot_usage: 'flexible' | 'full_dashboard'
  slot_range: [number, number]
  slots_required: number | null
  /** Natural display footprint: 1 for compact/summary, 2 for newsfeed/chart, 6 for full_dashboard */
  default_slots: number
  best_for_stakeholders: string[]
  capabilities: string[]
  features: string[]
  data_sources: string[]
  outputs: string[]
  /** Properties the reviewer can configure per-instance, e.g. ["title","newsfeed_type","tooltip"] */
  configurable_properties: string[]
  /** Allowed values for the newsfeed_type config property (empty = not a newsfeed widget) */
  newsfeed_type_options: string[]
  /** Pre-generated default tooltip text pre-populated in the ⚙ config panel */
  default_tooltip: string
  // Legacy fields kept for backwards compatibility
  supported_data_types: string[]
  supported_dimensions: string[]
  not_recommended_for: string[]
}

// ── Pipeline steps ────────────────────────────────────────────────────────────

export interface BriefBuilderStep {
  id: BriefStage
  label: string
  description: string
}

export const BRIEF_BUILDER_STEPS: BriefBuilderStep[] = [
  { id: 'intake',      label: 'Intake',          description: 'Enter client brief and stakeholders' },
  { id: 'interpreted', label: 'AI Interpretation', description: 'Review extracted requirements' },
  { id: 'reviewing',   label: 'Recommendations',  description: 'Review and edit dashboards & widgets' },
  { id: 'validated',   label: 'Configuration',    description: 'Configure AI modules and prompt IDs' },
  { id: 'configured',  label: 'Export',           description: 'Download tenant config YAML' },
]

export const BRIEF_ACTIVE_STEP: Record<BriefStage, number> = {
  intake:        0,
  interpreting:  0,
  interpreted:   1,
  recommending:  1,
  reviewing:     2,
  validating:    3,
  validated:     3,
  configured:    4,
  exporting:     4,
  complete:      4,
  error:         0,
}
